/**
 * Admin Refund Management Page
 *
 * Dashboard for managing refunds with filtering,
 * detail view, initiate refund, and retry failed refunds.
 *
 * API Endpoints:
 *   GET  /api/v1/admin/refunds
 *   GET  /api/v1/admin/refunds/:id
 *   POST /api/v1/admin/refunds
 *   POST /api/v1/admin/refunds/:id/retry
 *
 * Story: SCRUM-546 - Refund Processing UI
 * MVP: 1.21.6 - Guide Revenue Features
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiRequest from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/shared/Modal';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  Plus,
  RotateCcw
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
];

const REFUND_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'full', label: 'Full' },
  { value: 'partial', label: 'Partial' },
  { value: 'cancellation', label: 'Cancellation' },
  { value: 'dispute', label: 'Dispute' },
  { value: 'manual', label: 'Manual' },
];

const STATUS_BADGES = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
  succeeded: { label: 'Succeeded', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: XCircle },
  requires_action: { label: 'Action Required', color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
};

const REASON_OPTIONS = [
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'guide_unavailable', label: 'Guide Unavailable' },
  { value: 'weather_safety', label: 'Weather / Safety' },
  { value: 'medical_emergency', label: 'Medical Emergency' },
  { value: 'admin_override', label: 'Admin Override' },
];

export default function RefundManagement() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [refunds, setRefunds] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Detail modal
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Initiate refund modal
  const [showInitiateModal, setShowInitiateModal] = useState(false);
  const [initiateForm, setInitiateForm] = useState({
    bookingId: '',
    amountCents: '',
    reason: '',
    reasonDetails: '',
    refundType: 'manual',
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  const fetchRefunds = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (statusFilter) {params.set('status', statusFilter);}
      if (typeFilter) {params.set('refundType', typeFilter);}
      params.set('page', currentPage.toString());
      params.set('limit', '20');

      const response = await apiRequest(`/api/v1/admin/refunds?${params.toString()}`);

      if (response.success) {
        setRefunds(response.data.refunds || []);
        setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
        setStatusCounts(response.data.statusCounts || {});
      }
    } catch (err) {
      setError(err.message || 'Failed to load refunds');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, currentPage]);

  useEffect(() => {
    fetchRefunds();
  }, [fetchRefunds]);

  const fetchDetail = async (refundId) => {
    try {
      setDetailLoading(true);
      const response = await apiRequest(`/api/v1/admin/refunds/${refundId}`);
      if (response.success) {
        setSelectedRefund(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load refund details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleInitiateRefund = async () => {
    try {
      setActionLoading(true);
      setActionError(null);

      const amountCents = parseInt(initiateForm.amountCents, 10);
      if (!amountCents || amountCents <= 0) {
        setActionError('Amount must be a positive number');
        setActionLoading(false);
        return;
      }

      if (!initiateForm.bookingId.trim()) {
        setActionError('Booking ID is required');
        setActionLoading(false);
        return;
      }

      if (!initiateForm.reason) {
        setActionError('Reason is required');
        setActionLoading(false);
        return;
      }

      const response = await apiRequest('/api/v1/admin/refunds', {
        method: 'POST',
        body: JSON.stringify({
          bookingId: initiateForm.bookingId.trim(),
          amountCents,
          reason: initiateForm.reason,
          reasonDetails: initiateForm.reasonDetails.trim() || undefined,
          refundType: initiateForm.refundType,
        }),
      });

      if (response.success) {
        setShowInitiateModal(false);
        setInitiateForm({ bookingId: '', amountCents: '', reason: '', reasonDetails: '', refundType: 'manual' });
        fetchRefunds();
      } else {
        setActionError(response.error || 'Failed to initiate refund');
      }
    } catch (err) {
      setActionError(err.message || 'Failed to initiate refund');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetry = async (refundId) => {
    try {
      setActionLoading(true);
      setActionError(null);

      const response = await apiRequest(`/api/v1/admin/refunds/${refundId}/retry`, {
        method: 'POST',
      });

      if (response.success) {
        setSelectedRefund(null);
        fetchRefunds();
      } else {
        setActionError(response.error || 'Failed to retry refund');
      }
    } catch (err) {
      setActionError(err.message || 'Failed to retry refund');
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    if (!cents && cents !== 0) {return 'N/A';}
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) {return 'N/A';}
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) {return 'N/A';}
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const renderStatusBadge = (status) => {
    const config = STATUS_BADGES[status] || STATUS_BADGES.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="refunds"
      onNavigate={(id, path) => path && navigate(path)}
      user={{ name: authUser?.fullName || authUser?.firstName || 'Admin', email: authUser?.email || '', avatar: authUser?.profilePicture || null }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Refund Management</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setShowInitiateModal(true); setActionError(null); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark transition text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Initiate Refund
            </button>
            <button
              onClick={fetchRefunds}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition text-sm font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Status Count Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{statusCounts.pending || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Processing</p>
            <p className="text-2xl font-bold text-blue-600">{statusCounts.processing || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Succeeded</p>
            <p className="text-2xl font-bold text-green-600">{statusCounts.succeeded || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Failed</p>
            <p className="text-2xl font-bold text-red-600">{statusCounts.failed || 0}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              {REFUND_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
              <span className="ml-3 text-gray-600">Loading refunds...</span>
            </div>
          ) : refunds.length === 0 ? (
            <div className="text-center py-12">
              <RefreshCw className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No refunds found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Booking</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">Customer</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Guide</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Amount</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">Type</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Date</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {refunds.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-600">
                          {(r.bookingNumber || r.bookingId || '').toString().slice(0, 8)}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-gray-900">{r.customerName || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-gray-700">{r.guideName || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-green-600">
                          {formatCurrency(r.amountCents)}
                        </span>
                      </td>
                      <td className="px-4 py-3">{renderStatusBadge(r.status)}</td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="capitalize text-gray-600 text-xs">{r.refundType}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs hidden md:table-cell">
                        {formatDate(r.createdAt || r.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => fetchDetail(r.id)}
                          className="inline-flex items-center gap-1 text-brand-primary hover:text-brand-dark text-xs font-medium"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="p-1.5 rounded border border-gray-300 disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage >= pagination.totalPages}
                  className="p-1.5 rounded border border-gray-300 disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        <Modal
          isOpen={!!selectedRefund}
          onClose={() => { setSelectedRefund(null); setActionError(null); }}
          title="Refund Details"
          footer={
            <>
              <button
                onClick={() => { setSelectedRefund(null); setActionError(null); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Close
              </button>
              {selectedRefund?.status === 'failed' && (
                <button
                  onClick={() => handleRetry(selectedRefund.id)}
                  disabled={actionLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 transition"
                >
                  <RotateCcw className="w-4 h-4" />
                  {actionLoading ? 'Retrying...' : 'Retry Refund'}
                </button>
              )}
            </>
          }
        >
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            </div>
          ) : selectedRefund ? (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-gray-500">Status</span>
                  <div className="mt-1">{renderStatusBadge(selectedRefund.status)}</div>
                </div>
                <div>
                  <span className="text-gray-500">Amount</span>
                  <p className="font-medium text-green-600">{formatCurrency(selectedRefund.amountCents)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Booking</span>
                  <p className="font-mono text-xs">{selectedRefund.bookingNumber || selectedRefund.bookingId}</p>
                </div>
                <div>
                  <span className="text-gray-500">Type</span>
                  <p className="font-medium capitalize">{selectedRefund.refundType}</p>
                </div>
                <div>
                  <span className="text-gray-500">Customer</span>
                  <p className="font-medium">{selectedRefund.customerName || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Guide</span>
                  <p className="font-medium">{selectedRefund.guideName || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Experience</span>
                  <p className="font-medium">{selectedRefund.experienceTitle || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Initiated By</span>
                  <p className="font-medium">{selectedRefund.initiatorName || 'System'}</p>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500 uppercase mb-2">Financial Summary</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-gray-500 text-xs">Booking Total</span>
                    <p className="font-medium">{formatCurrency(selectedRefund.bookingTotalPriceCents)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs">This Refund</span>
                    <p className="font-medium text-green-600">{formatCurrency(selectedRefund.amountCents)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs">Total Refunded</span>
                    <p className="font-medium">{formatCurrency(selectedRefund.totalRefundedForBookingCents)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs">Remaining Refundable</span>
                    <p className="font-medium">{formatCurrency(selectedRefund.remainingRefundableCents)}</p>
                  </div>
                </div>
              </div>

              {/* Reason */}
              {selectedRefund.reason && (
                <div>
                  <span className="text-gray-500">Reason</span>
                  <p className="mt-1 p-3 bg-gray-50 rounded-md">{selectedRefund.reason}</p>
                </div>
              )}

              {/* Error info for failed refunds */}
              {selectedRefund.status === 'failed' && (
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-red-600 uppercase mb-1">Error Details</p>
                  <p className="text-sm text-red-700">{selectedRefund.errorMessage || 'Unknown error'}</p>
                  {selectedRefund.errorCode && (
                    <p className="text-xs text-red-500 mt-1">Code: {selectedRefund.errorCode}</p>
                  )}
                </div>
              )}

              {/* Stripe info */}
              {selectedRefund.stripeRefundId && (
                <div>
                  <span className="text-gray-500">Stripe Refund ID</span>
                  <p className="font-mono text-xs mt-1">{selectedRefund.stripeRefundId}</p>
                </div>
              )}

              {/* Timeline */}
              <div>
                <span className="text-gray-500">Timeline</span>
                <div className="mt-2 space-y-2">
                  {selectedRefund.createdAt && (
                    <div className="flex items-center space-x-2 text-xs">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-gray-600">Created: {formatDateTime(selectedRefund.createdAt)}</span>
                    </div>
                  )}
                  {selectedRefund.processedAt && (
                    <div className="flex items-center space-x-2 text-xs">
                      <RefreshCw className="w-3 h-3 text-blue-500" />
                      <span className="text-gray-600">Processing: {formatDateTime(selectedRefund.processedAt)}</span>
                    </div>
                  )}
                  {selectedRefund.completedAt && (
                    <div className="flex items-center space-x-2 text-xs">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      <span className="text-gray-600">Completed: {formatDateTime(selectedRefund.completedAt)}</span>
                    </div>
                  )}
                  {selectedRefund.failedAt && (
                    <div className="flex items-center space-x-2 text-xs">
                      <XCircle className="w-3 h-3 text-red-500" />
                      <span className="text-gray-600">Failed: {formatDateTime(selectedRefund.failedAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              {actionError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{actionError}</p>
                </div>
              )}
            </div>
          ) : null}
        </Modal>

        {/* Initiate Refund Modal */}
        <Modal
          isOpen={showInitiateModal}
          onClose={() => { setShowInitiateModal(false); setActionError(null); }}
          title="Initiate Refund"
          footer={
            <>
              <button
                onClick={() => { setShowInitiateModal(false); setActionError(null); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleInitiateRefund}
                disabled={actionLoading || !initiateForm.bookingId || !initiateForm.amountCents || !initiateForm.reason}
                className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 transition"
              >
                {actionLoading ? 'Processing...' : 'Initiate Refund'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Booking ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={initiateForm.bookingId}
                onChange={(e) => setInitiateForm({ ...initiateForm, bookingId: e.target.value })}
                placeholder="Enter booking UUID"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (cents) <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={initiateForm.amountCents}
                  onChange={(e) => setInitiateForm({ ...initiateForm, amountCents: e.target.value })}
                  placeholder="e.g. 27000"
                  className="w-40 px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                {initiateForm.amountCents && (
                  <span className="text-sm text-gray-500">
                    = {formatCurrency(parseInt(initiateForm.amountCents, 10) || 0)}
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <select
                value={initiateForm.reason}
                onChange={(e) => setInitiateForm({ ...initiateForm, reason: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">Select reason...</option>
                {REASON_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Details (optional)
              </label>
              <textarea
                value={initiateForm.reasonDetails}
                onChange={(e) => setInitiateForm({ ...initiateForm, reasonDetails: e.target.value })}
                placeholder="Add details about this refund..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Refund Type
              </label>
              <select
                value={initiateForm.refundType}
                onChange={(e) => setInitiateForm({ ...initiateForm, refundType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                {REFUND_TYPE_OPTIONS.filter((opt) => opt.value).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {actionError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{actionError}</p>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </DashboardLayout>
  );
}
